import React, { useState } from 'react';
import { useDevelopmentDemo } from '../../contexts/DevelopmentDemoContext';
import { criterionLevelLabels, illustrativeRubricNote } from '../../data/developmentDomains';
import type { CriterionLevel, ReviewDraft, ReviewOutcome } from '../../types/developmentDemo';
import { DemoSectionHeader } from '../demoShell/DemoSectionHeader';
import { formatTimestamp } from '../../utils/demoStorage';
import { errorClass, fieldClass, focusRing, glassPanel, labelClass, primaryButton } from '../../utils/demoUi';
import { emptyReviewDraft, participantName, validateReviewDraft } from '../../utils/developmentDemo';
const levels: CriterionLevel[] = ['not-yet', 'partly', 'evidenced'];
const outcomes: {
  id: ReviewOutcome;
  label: string;
}[] = [{
  id: 'reviewed',
  label: 'Reviewed'
}, {
  id: 'revision-requested',
  label: 'Revision requested'
}];
export function EvidenceReview() {
  const {
    state,
    config,
    dispatch
  } = useDevelopmentDemo();
  const pending = state.submissions.filter(submission => !state.reviews.some(review => review.submissionId === submission.id));
  const selected = pending.find(submission => submission.id === state.ui.selectedSubmissionId) ?? pending[0];
  const draft: ReviewDraft = selected && state.ui.reviewDrafts[selected.id] || emptyReviewDraft(config);
  const [attempted, setAttempted] = useState(false);
  const [message, setMessage] = useState('');
  const errors = attempted ? validateReviewDraft(config, draft) : {};
  const update = (patch: Partial<ReviewDraft>) => {
    if (!selected) return;
    setMessage('');
    dispatch({
      type: 'setReviewDraft',
      submissionId: selected.id,
      draft: {
        ...draft,
        ...patch
      }
    });
  };
  const updateObservation = (criterionId: string, patch: Partial<ReviewDraft['observations'][string]>) => update({
    observations: {
      ...draft.observations,
      [criterionId]: {
        ...draft.observations[criterionId],
        ...patch
      }
    }
  });
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    setAttempted(true);
    if (Object.keys(validateReviewDraft(config, draft)).length > 0) return;
    dispatch({
      type: 'submitReview',
      submissionId: selected.id
    });
    setAttempted(false);
    setMessage(`Review saved for ${selected.id} (${participantName(config, selected.participantId)}).`);
  };
  return <div data-id="element-730" data-mp="1kug92sks0e3w">
      <DemoSectionHeader title="Evidence Review" description="Read the response beside the criteria. Record an observation for every criterion and explain the evidence in your feedback." data-id="element-731" data-mp="1kupzuidnapan" />
      <p role="status" className="mb-4 text-sm font-medium text-navy" data-id="element-732" data-mp="1ulavbwy0vdpc">
        {message}
      </p>

      {pending.length === 0 || !selected ? <div className={`${glassPanel} p-10 text-center`} data-id="element-733" data-mp="28jre4m95sunu">
          <p className="font-heading font-bold text-ink-dark" data-id="element-734" data-mp="23l7q8gb098su">No responses awaiting review</p>
          <p className="mt-2 text-sm text-ink-muted" data-id="element-735" data-mp="e5zwazgof4hb">New and revised responses appear here as they are submitted.</p>
        </div> : <>
          {pending.length > 1 && <div role="group" aria-label="Responses awaiting review" className="mb-6 flex flex-wrap gap-2" data-id="element-736" data-mp="2kv1sdgci7hsw">
              {pending.map(submission => <button key={submission.id} type="button" aria-pressed={submission.id === selected.id} onClick={() => dispatch({
          type: 'selectSubmission',
          id: submission.id
        })} className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors duration-150 ${focusRing} ${submission.id === selected.id ? 'border-navy bg-navy text-white' : 'border-line bg-white text-ink-dark hover:bg-surface-muted'}`} data-id="element-737" data-mp="2pt3bu2dudj4w">
                  {participantName(config, submission.participantId)} · {submission.id}
                </button>)}
            </div>}

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2" data-id="element-738" data-mp="3rj8gbp7aoqbd">
            <section aria-labelledby="response-title" className={`${glassPanel} p-6 xl:sticky xl:top-28 xl:self-start`} data-id="element-739" data-mp="3rifixeu87d5w">
              <p className="text-xs text-ink-muted" data-id="element-740" data-mp="32q87w4rjsbe3">
                <span className="font-mono font-semibold" data-id="element-741" data-mp="2xusxjmsr0xmf">{selected.id}</span> · {selected.round === 'initial' ? 'Initial task' : 'Reassessment'} · Task{' '}
                {selected.taskVersionId} · {formatTimestamp(selected.at)}
              </p>
              <h2 id="response-title" className="mt-1 font-heading text-xl font-bold text-ink-dark" data-id="element-742" data-mp="2nxobdmywl25m">
                {participantName(config, selected.participantId)}
              </h2>
              <p className="mt-3 text-sm text-ink-muted" data-id="element-743" data-mp="2f0x4sgmq64ws">{config.tasks[selected.round].context}</p>
              <p className="mt-2 text-sm font-semibold text-ink-dark" data-id="element-744" data-mp="2a1sybxr6qla5">{config.tasks[selected.round].prompt}</p>
              <div className="mt-4 rounded-lg border-l-4 border-navy/40 bg-surface-bg px-4 py-3" data-id="element-745" data-mp="252zk2342qq8x">
                <p className="text-xs font-semibold text-ink-muted" data-id="element-746" data-mp="207k8jhtjh6fo">Original response</p>
                <p className="mt-1 whitespace-pre-wrap leading-relaxed text-ink-dark" data-id="element-747" data-mp="1w68lnk21uzb6">{selected.response}</p>
              </div>
            </section>

            <form onSubmit={submit} noValidate className={`${glassPanel} p-6`} data-id="element-748" data-mp="fmx535etctxr">
              {config.criteria.map(criterion => {
            const entry = draft.observations[criterion.id] ?? {
              level: '',
              observation: ''
            };
            return <fieldset key={criterion.id} className="border-b border-line pb-5 pt-1 mb-5 last-of-type:mb-0" data-id="element-749" data-mp="pjiavsynq92n">
                    <legend className="font-heading font-bold text-ink-dark" data-id="element-750" data-mp="3t2bmw1bsepup">{criterion.label}</legend>
                    <p className="text-sm text-ink-muted" data-id="element-751" data-mp="1wjwr1y0fd7w">{criterion.description}</p>
                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3" data-id="element-752" data-mp="6n0egy8v5l02">
                      {levels.map(level => <label key={level} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${entry.level === level ? 'border-lavender bg-lavender-pale' : 'border-line bg-white'}`} data-id="element-753" data-mp="35dsellsqmd2m">
                          <input type="radio" name={`${selected.id}-${criterion.id}`} checked={entry.level === level} onChange={() => updateObservation(criterion.id, {
                    level
                  })} className="h-4 w-4 accent-[#6366C9]" data-id="element-754" data-mp="2y7zpezmihld3" />
                          {criterionLevelLabels[level]}
                        </label>)}
                    </div>
                    {errors[`${criterion.id}-level`] && <p className={errorClass} data-id="element-755" data-mp="2oalcvb3plgq2">{errors[`${criterion.id}-level`]}</p>}
                    <label htmlFor={`${selected.id}-${criterion.id}-obs`} className={`mt-3 ${labelClass}`} data-id="element-756" data-mp="8jpcasq49pyl">
                      Observation
                    </label>
                    <textarea id={`${selected.id}-${criterion.id}-obs`} rows={2} value={entry.observation} onChange={event => updateObservation(criterion.id, {
                observation: event.target.value
              })} aria-invalid={Boolean(errors[`${criterion.id}-observation`])} className={fieldClass} data-id="element-757" data-mp="1lj0mxyca4v3m" />
                    {errors[`${criterion.id}-observation`] && <p className={errorClass} data-id="element-758" data-mp="25949seewc7ke">{errors[`${criterion.id}-observation`]}</p>}
                  </fieldset>;
          })}

              <label htmlFor="review-feedback" className={labelClass} data-id="element-759" data-mp="1vhzvzi0yisr5">
                Feedback explaining the evidence
              </label>
              <textarea id="review-feedback" rows={4} value={draft.feedback} onChange={event => update({
            feedback: event.target.value
          })} aria-invalid={Boolean(errors.feedback)} className={fieldClass} data-id="element-760" data-mp="20eghe4grut0j" />
              {errors.feedback && <p className={errorClass} data-id="element-761" data-mp="o3w5kdtca96g">{errors.feedback}</p>}

              <label htmlFor="review-questions" className={`mt-4 ${labelClass}`} data-id="element-762" data-mp="o2b92ingsg5y">
                {config.features.comparison ? 'Unanswered questions' : 'Open questions'} <span className="font-normal text-ink-muted" data-id="element-763" data-mp="e681uydr9rc5">(optional)</span>
              </label>
              <textarea id="review-questions" rows={2} value={draft.openQuestions} onChange={event => update({
            openQuestions: event.target.value
          })} className={fieldClass} data-id="element-764" data-mp="1btgsf7sm5ql0" />

              <fieldset className="mt-5" data-id="element-765" data-mp="11ur9dboze036">
                <legend className={labelClass} data-id="element-766" data-mp="11tpz39fshhmi">Outcome</legend>
                <div className="mt-2 flex flex-wrap gap-2" data-id="element-767" data-mp="xtyx09ktbf2o">
                  {outcomes.map(outcome => <label key={outcome.id} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold ${draft.outcome === outcome.id ? 'border-lavender bg-lavender-pale' : 'border-line bg-white'}`} data-id="element-768" data-mp="3mli9em0ezorb">
                      <input type="radio" name={`${selected.id}-outcome`} checked={draft.outcome === outcome.id} onChange={() => update({
                  outcome: outcome.id
                })} className="h-4 w-4 accent-[#6366C9]" data-id="element-769" data-mp="220uzprv3ig0y" />
                      {outcome.label}
                    </label>)}
                </div>
                <p className="mt-2 text-xs text-ink-muted" data-id="element-770" data-mp="2ptv19nk4t4ge">A revision request lets the {config.participantNoun.toLowerCase()} respond again; this response and review are kept.</p>
                {errors.outcome && <p className={errorClass} data-id="element-771" data-mp="vfk8j40f1q5c">{errors.outcome}</p>}
              </fieldset>

              <button type="submit" className={`mt-6 ${primaryButton}`} data-id="element-772" data-mp="10dc5wdjty4gx">
                Save review
              </button>
              <p className="mt-3 text-xs text-ink-muted" data-id="element-773" data-mp="2k5gjvsh5uvc4">{illustrativeRubricNote}</p>
            </form>
          </div>
        </>}
    </div>;
}